import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";

const CARDS = [
  { tk: "markets.built.card1.title", dk: "markets.built.card1.desc" },
  { tk: "markets.built.card2.title", dk: "markets.built.card2.desc" },
  { tk: "markets.built.card3.title", dk: "markets.built.card3.desc" },
  { tk: "markets.built.card4.title", dk: "markets.built.card4.desc" },
  { tk: "markets.built.card5.title", dk: "markets.built.card5.desc" },
  { tk: "markets.built.card6.title", dk: "markets.built.card6.desc" },
];

function Card({ titleKey, descKey }) {
  const { t } = useTranslation();
  return (
    <div className="bg-[#fcefea] rounded-[20px] px-[30px] pt-[30px] pb-[51px] flex flex-col gap-[40px]">
      <div className="bg-[#ee5414] rounded-[15px] xl:rounded-[25px] size-[30px] xl:size-[50px] flex items-center justify-center shrink-0">
        <Check size={16} className="text-white xl:hidden" strokeWidth={3} />
        <Check size={24} className="text-white hidden xl:block" strokeWidth={3} />
      </div>
      <div className="flex flex-col gap-[12px]">
        <p className="font-['Montserrat'] font-extrabold text-[16px] xl:text-[20px] text-[#020202] leading-normal">
          {t(titleKey)}
        </p>
        <p className="font-['Inter'] font-normal text-[14px] xl:text-[16px] text-[#717171] leading-[24px]">
          {t(descKey)}
        </p>
      </div>
    </div>
  );
}

function BuiltFor() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const prev = () => {
    setDirection(-1);
    setActiveIndex((i) => Math.max(0, i - 1));
  };

  const next = () => {
    setDirection(1);
    setActiveIndex((i) => Math.min(CARDS.length - 1, i + 1));
  };

  const goTo = (index) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };

  return (
    <section className="mt">
      <div className="glcontainer flex flex-col gap-[40px] xl:gap-[60px]">
        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-[20px]">
          <h2 className="font-['Montserrat'] font-black text-[24px] xl:text-[46px] text-[#0a0a0a] tracking-[-0.92px] uppercase leading-none xl:max-w-[504px]">
            {t("markets.built.title")}
          </h2>
          <p className="font-['Inter'] font-normal text-[14px] xl:text-[18px] text-[#717171] leading-[1.4] xl:max-w-[600px]">
            {t("markets.built.desc")}
          </p>
        </div>

        {/* Desktop grid */}
        <div className="hidden sm:grid sm:grid-cols-2 xl:grid-cols-3 gap-[20px] xl:gap-[21px]">
          {CARDS.map((card, i) => (
            <Card key={i} titleKey={card.tk} descKey={card.dk} />
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="sm:hidden flex flex-col gap-[20px]">
          <div
            key={activeIndex}
            className={`swiper-card-enter ${direction > 0 ? "swiper-card-next" : "swiper-card-prev"}`}
          >
            <Card titleKey={CARDS[activeIndex].tk} descKey={CARDS[activeIndex].dk} />
          </div>
          <div className="flex items-center justify-between rounded-[20px] h-[28px] px-[18px] py-[4px]">
            <button onClick={prev} disabled={activeIndex === 0} className="flex items-center justify-center w-[28px] h-[20px] disabled:opacity-30" aria-label="Previous">
              <ChevronLeft size={16} className="text-[#707070]" />
            </button>
            <div className="flex items-center gap-[8px]">
              {CARDS.map((_, i) => (
                <button key={i} onClick={() => goTo(i)} className={`w-[8px] h-[8px] rounded-full transition-colors duration-200 ${i === activeIndex ? "bg-[#ee5414]" : "bg-[#d9d9d9]"}`} aria-label={`Slide ${i + 1}`} />
              ))}
            </div>
            <button onClick={next} disabled={activeIndex === CARDS.length - 1} className="flex items-center justify-center w-[28px] h-[20px] disabled:opacity-30" aria-label="Next">
              <ChevronRight size={16} className="text-[#707070]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BuiltFor;
